import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { easeOut, fadeUp } from "../motion";
import { IconChevron, IconChevronLeft, IconChevronRight } from "../components/icons";
import { MiniCalendar } from "../components/MiniCalendar";
import { DayNoteEditor } from "../components/DayNoteEditor";
import { DoneTodayStrip } from "../components/DoneTodayStrip";
import { EndOfDayDrawer } from "../components/EndOfDayDrawer";
import { useAgenda } from "../hooks/useAgenda";
import { useDayNote } from "../hooks/useDayNote";
import { useHomes } from "../hooks/useSettings";
import { hkToday, shiftDate } from "../lib/dates";
import "../styles/dashboard.css";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function fmtLong(date: string): string {
  return new Date(`${date}T00:00:00+08:00`).toLocaleDateString("en-GB", {
    weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Hong_Kong",
  });
}

const time = (t: string) => t.slice(0, 5);

/**
 * The Dashboard is the Day-note page (ADR-0009): the landing page is the daily
 * writing surface — one note per HK calendar day, with that day's actions
 * (shift + completed to-dos + today's due counts) on the page. Obsidian-style:
 * a plain canvas with Markdown shortcuts, no toolbar, no card.
 * The same component serves /log/:date? for specific days via DailyLogPage.
 * The page stays mounted across day changes (AppShell keys the transition by
 * page, not by the date param), so per-day state is crossfaded here instead.
 */
export function DashboardPage() {
  const params = useParams<{ date?: string }>();
  const navigate = useNavigate();
  const today = hkToday();
  const date = params.date && DATE_RE.test(params.date) ? params.date : today;
  const isToday = date === today;
  const [eodOpen, setEodOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const pickDay = (d: string) => {
    setCalendarOpen(false);
    navigate(`/log/${d}`);
  };

  const { query: log, content, status, savedAt, restoredDraft, handleChange, retry } = useDayNote(date);
  const { data: agenda } = useAgenda();
  const { data: homes } = useHomes();

  // The page no longer remounts on day changes — close any open End-of-day
  // drawer (it is about "today") when the selected day moves.
  useEffect(() => setEodOpen(false), [date]);

  const homeName = (id: string | null) => homes?.find((h) => h.id === id)?.name;
  // Home identity dot: same index-based palette as RotaPage/TodosPage (hdot-0..5).
  const homeIndex = (id: string) => homes?.findIndex((h) => h.id === id) ?? -1;
  // The clock is always rendered (on every day) so the time is consistently
  // first on the hero line and the row height never changes — the strip/card
  // below don't jump when switching days.
  const clock = new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const shifts = log.data?.shifts ?? [];
  const savedText =
    savedAt !== null ? `Saved ${new Date(savedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : "";

  return (
    <div className="note-page">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={date}
          className="note-day"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18, ease: easeOut }}
        >
          <motion.header className="note-hero" variants={fadeUp} initial="hidden" animate="show">
            <div className="note-hero-meta">
              <span className="note-clock" aria-label="Current time">{clock}</span>
              <div className="note-date-wrap">
                <h1 className="note-date-head">
                  <button
                    type="button"
                    className="note-date"
                    aria-haspopup="dialog"
                    aria-expanded={calendarOpen}
                    aria-label={`Open calendar — currently ${fmtLong(date)}`}
                    onClick={() => setCalendarOpen((o) => !o)}
                  >
                    {fmtLong(date)}
                    <span className="note-date-caret" aria-hidden="true"><IconChevron /></span>
                  </button>
                </h1>
                <MiniCalendar open={calendarOpen} date={date} onSelect={pickDay} onClose={() => setCalendarOpen(false)} />
              </div>
              {!log.isLoading && log.data ? (
                shifts.length > 0 ? (
                  <span className="hero-shifts">
                    {shifts.map((s) => (
                      <span key={s.homeId} className="hero-shift">
                        <span className={`hdot hdot-${Math.abs(homeIndex(s.homeId)) % 6}`} aria-hidden />{s.homeName} <span className="muted">{time(s.startTime)}–{time(s.endTime)}</span>
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="hero-shift muted">無報更</span>
                )
              ) : null}
            </div>
            <div className="note-hero-actions">
              <nav className="log-nav" aria-label="Choose a day">
                <button type="button" className="btn btn-secondary btn-icon" aria-label="Previous day" onClick={() => navigate(`/log/${shiftDate(date, -1)}`)}>
                  <IconChevronLeft />
                </button>
                <button type="button" className="btn btn-secondary btn-icon" aria-label="Next day" onClick={() => navigate(`/log/${shiftDate(date, 1)}`)}>
                  <IconChevronRight />
                </button>
              </nav>
              <span className="day-toggle">
                <button
                  type="button"
                  className={`btn btn-secondary day-toggle-btn${isToday ? " is-on" : ""}`}
                  onClick={() => setEodOpen(true)}
                >
                  End of day
                </button>
                <button
                  type="button"
                  className={`btn btn-ghost day-toggle-btn${isToday ? "" : " is-on"}`}
                  onClick={() => navigate("/")}
                >
                  Today
                </button>
              </span>
            </div>
          </motion.header>

          <DoneTodayStrip
            log={log.data}
            loading={log.isLoading}
            isToday={isToday}
            homeName={homeName}
            totals={isToday ? agenda?.totals : undefined}
          />

          {restoredDraft ? <p className="note-restored">Restored an unsaved draft — saving now.</p> : null}

          <section className="note-write">
            <DayNoteEditor value={content ?? ""} onChange={handleChange} />
            <div className="note-write-foot">
              {status === "error" ? (
                <button type="button" className="note-status error" onClick={retry}>Not saved — tap to retry</button>
              ) : (
                <span className={`note-status ${status}`} role="status">
                  {status === "saving" ? "Saving…" : status === "saved" ? savedText : ""}
                </span>
              )}
            </div>
          </section>
        </motion.div>
      </AnimatePresence>

      <EndOfDayDrawer open={eodOpen} onClose={() => setEodOpen(false)} />
    </div>
  );
}
